import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import CloseIcon from '@mui/icons-material/Close';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import CircularProgress from '@mui/material/CircularProgress';
import { ButtonPro, Typography } from 'components';
import { agent } from 'services/service';
import {
  AttendanceSettings,
  AttendanceUploadAssignee,
  AttendanceUploadAssigneeOption
} from 'services/AttendanceTrackingService';
import { toast } from 'react-toastify';
import {
  formatAttendanceSavedAt,
  formatSavedByLabel
} from 'utils/formatAttendanceLastImport';
import {
  HOURS_STEP,
  MAX_HOURS_THRESHOLD,
  validateOfficeDayRules
} from 'utils/attendanceOfficeRules';
import './index.scss';

interface SettingsFormState {
  minimumOfficeDaysPerMonth: number;
  fullDayHoursThreshold: number;
  shortDayHoursThreshold: number;
  assignees: AttendanceUploadAssignee[];
}

const mapSettingsToForm = (
  settings: AttendanceSettings
): SettingsFormState => ({
  minimumOfficeDaysPerMonth: settings.minimumOfficeDaysPerMonth,
  fullDayHoursThreshold: settings.fullDayHoursThreshold,
  shortDayHoursThreshold: settings.shortDayHoursThreshold,
  assignees: [...settings.uploadAssignees]
});

const assigneeKey = (assignees: AttendanceUploadAssignee[]) =>
  [...assignees]
    .map((assignee) => assignee.userRoleId)
    .sort()
    .join(',');

const getAssigneeName = (assignee: AttendanceUploadAssignee) =>
  assignee.displayLabel || assignee.employeeName;

const getInitials = (name: string) =>
  name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('');

const getOptionDesignation = (option: AttendanceUploadAssigneeOption) =>
  option.designation || option.role;

const PAGE_SIZE = 50;

interface Props {
  onSaved?: (settings: AttendanceSettings) => void;
}

export const AttendanceSettingsForm = ({ onSaved }: Props) => {
  const [initial, setInitial] = useState<SettingsFormState | null>(null);
  const [form, setForm] = useState<SettingsFormState | null>(null);
  const [updatedMeta, setUpdatedMeta] = useState<{
    updatedAt?: string;
    updatedByUpn?: string;
    updatedByDisplayName?: string;
  }>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<
    AttendanceUploadAssigneeOption[]
  >([]);
  const [searchTotalCount, setSearchTotalCount] = useState(0);
  const [searchPage, setSearchPage] = useState(1);
  const [searchLoading, setSearchLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const loadSettings = useCallback(() => {
    setLoading(true);
    agent.AttendanceTrackingService.getSettings()
      .then((response) => {
        const nextForm = mapSettingsToForm(response.data);
        setInitial(nextForm);
        setForm(nextForm);
        setUpdatedMeta({
          updatedAt: response.data.updatedAt,
          updatedByUpn: response.data.updatedByUpn,
          updatedByDisplayName: response.data.updatedByDisplayName
        });
      })
      .catch((err: any) => {
        const message =
          err.response?.data?.message ||
          err.message ||
          'Failed to load attendance settings.';
        toast.error(message);
      })
      .finally(() => setLoading(false));
  }, []);

  const loadAssigneeOptions = useCallback(
    async (search: string, page: number, append: boolean) => {
      if (page === 1) {
        setSearchLoading(true);
      } else {
        setLoadingMore(true);
      }

      try {
        const response =
          await agent.AttendanceTrackingService.searchUploadAssigneeOptions(
            search.trim(),
            page,
            PAGE_SIZE
          );

        setSearchTotalCount(response.data.totalCount);
        setSearchPage(page);
        setSearchResults((current) => {
          if (!append) {
            return response.data.items;
          }

          const merged = new Map(
            current.map((item) => [item.userRoleId, item])
          );
          response.data.items.forEach((item) => {
            merged.set(item.userRoleId, item);
          });
          return Array.from(merged.values());
        });
      } catch {
        if (!append) {
          setSearchResults([]);
          setSearchTotalCount(0);
        }
      } finally {
        setSearchLoading(false);
        setLoadingMore(false);
      }
    },
    []
  );

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  useEffect(() => {
    if (!dropdownOpen) return;

    const delay = searchTerm.trim() ? 300 : 0;
    const timer = window.setTimeout(() => {
      loadAssigneeOptions(searchTerm, 1, false);
    }, delay);

    return () => window.clearTimeout(timer);
  }, [searchTerm, dropdownOpen, loadAssigneeOptions]);

  const hasMoreAssigneeOptions = searchResults.length < searchTotalCount;

  const handleAssigneeMenuScroll = (event: React.UIEvent<HTMLDivElement>) => {
    if (searchLoading || loadingMore || !hasMoreAssigneeOptions) return;

    const element = event.currentTarget;
    const nearBottom =
      element.scrollTop + element.clientHeight >= element.scrollHeight - 48;

    if (nearBottom) {
      loadAssigneeOptions(searchTerm, searchPage + 1, true);
    }
  };

  const renderAssigneeOption = (option: AttendanceUploadAssigneeOption) => {
    if (!form) return null;

    const alreadySelected = form.assignees.some(
      (assignee) => assignee.userRoleId === option.userRoleId
    );
    const disabled =
      option.isAlreadyAssigned ||
      option.hasImplicitUploadAccess ||
      alreadySelected;

    return (
      <button
        key={option.userRoleId}
        type="button"
        role="option"
        aria-selected={alreadySelected}
        className={`attendance-settings__dropdown-option${
          disabled ? ' attendance-settings__dropdown-option--disabled' : ''
        }`}
        disabled={disabled}
        title={
          disabled
            ? option.hasImplicitUploadAccess
              ? 'Already has upload access'
              : 'Already assigned'
            : undefined
        }
        onClick={() => handleAddAssignee(option)}
      >
        <span className="attendance-settings__dropdown-option-name">
          {option.displayLabel || option.employeeName}
        </span>
        <span className="attendance-settings__dropdown-option-meta">
          {option.hasImplicitUploadAccess
            ? 'Already has upload access'
            : option.isAlreadyAssigned || alreadySelected
              ? 'Already assigned'
              : getOptionDesignation(option)}
        </span>
      </button>
    );
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchContainerRef.current &&
        !searchContainerRef.current.contains(event.target as Node)
      ) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fieldErrors = useMemo(
    () => (form ? validateOfficeDayRules(form) : {}),
    [form]
  );

  const isDirty = useMemo(() => {
    if (!initial || !form) return false;

    return (
      initial.minimumOfficeDaysPerMonth !== form.minimumOfficeDaysPerMonth ||
      initial.fullDayHoursThreshold !== form.fullDayHoursThreshold ||
      initial.shortDayHoursThreshold !== form.shortDayHoursThreshold ||
      assigneeKey(initial.assignees) !== assigneeKey(form.assignees)
    );
  }, [initial, form]);

  const hasErrors = Object.keys(fieldErrors).length > 0;

  const openDropdown = () => {
    setDropdownOpen(true);
    window.setTimeout(() => searchInputRef.current?.focus(), 0);
  };

  const closeDropdown = () => {
    setDropdownOpen(false);
    setSearchTerm('');
    setSearchResults([]);
    setSearchTotalCount(0);
    setSearchPage(1);
  };

  const handleCancel = () => {
    if (!initial) return;
    setForm({ ...initial, assignees: [...initial.assignees] });
    closeDropdown();
  };

  const handleSave = async () => {
    if (!form || !isDirty || hasErrors) return;

    setSaving(true);
    try {
      const response = await agent.AttendanceTrackingService.updateSettings({
        minimumOfficeDaysPerMonth: form.minimumOfficeDaysPerMonth,
        fullDayHoursThreshold: form.fullDayHoursThreshold,
        shortDayHoursThreshold: form.shortDayHoursThreshold,
        uploadAssigneeUserRoleIds: form.assignees.map(
          (assignee) => assignee.userRoleId
        )
      });

      const nextForm = mapSettingsToForm(response.data);
      setInitial(nextForm);
      setForm(nextForm);
      setUpdatedMeta({
        updatedAt: response.data.updatedAt,
        updatedByUpn: response.data.updatedByUpn,
        updatedByDisplayName: response.data.updatedByDisplayName
      });
      onSaved?.(response.data);
      toast.success('Attendance settings saved.');
    } catch (err: any) {
      const message =
        err.response?.data?.message ||
        err.message ||
        'Failed to save attendance settings.';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleAddAssignee = (option: AttendanceUploadAssigneeOption) => {
    if (!form) return;
    if (
      option.isAlreadyAssigned ||
      option.hasImplicitUploadAccess ||
      form.assignees.some(
        (assignee) => assignee.userRoleId === option.userRoleId
      )
    ) {
      return;
    }

    setForm({
      ...form,
      assignees: [
        ...form.assignees,
        {
          userRoleId: option.userRoleId,
          employeeName: option.employeeName,
          epfNumber: option.epfNumber,
          role: option.role,
          displayLabel: option.displayLabel
        }
      ]
    });
    closeDropdown();
  };

  const handleRemoveAssignee = (userRoleId: string) => {
    if (!form) return;
    setForm({
      ...form,
      assignees: form.assignees.filter(
        (assignee) => assignee.userRoleId !== userRoleId
      )
    });
  };

  if (loading) {
    return (
      <div className="attendance-settings__loading">
        <CircularProgress size={32} />
        <Typography type="base_medium" text="Loading settings..." />
      </div>
    );
  }

  if (!form) {
    return (
      <div className="attendance-settings__loading">
        <Typography
          type="base_medium"
          text="Unable to load attendance settings."
        />
        <ButtonPro label="Retry" onClick={loadSettings} />
      </div>
    );
  }

  return (
    <div className="attendance-settings">
      <div className="attendance-settings__grid">
        <section className="attendance-settings__card attendance-settings__card--rules">
          <div className="attendance-settings__card-header">
            <span className="attendance-settings__card-icon" aria-hidden>
              <TuneOutlinedIcon fontSize="small" />
            </span>
            <div>
              <Typography
                type="heading4"
                text="Office day rules"
                weight="bold"
              />
              <Typography
                type="small_regular"
                text="Thresholds classify daily attendance and monthly compliance across the attendance module."
                className="attendance-settings__card-hint"
              />
            </div>
          </div>

          <div className="attendance-settings__rules">
            <div className="attendance-settings__rule">
              <label
                className="attendance-settings__rule-label"
                htmlFor="minimumOfficeDays"
              >
                Minimum office days
              </label>
              <div className="attendance-settings__rule-control">
                <input
                  id="minimumOfficeDays"
                  type="number"
                  min={1}
                  max={31}
                  className={`attendance-settings__input${
                    fieldErrors.minimumOfficeDaysPerMonth
                      ? ' attendance-settings__input--error'
                      : ''
                  }`}
                  value={form.minimumOfficeDaysPerMonth}
                  onChange={(event) =>
                    setForm({
                      ...form,
                      minimumOfficeDaysPerMonth: Number(event.target.value)
                    })
                  }
                />
                <span className="attendance-settings__unit">days / month</span>
              </div>
              {fieldErrors.minimumOfficeDaysPerMonth && (
                <span className="attendance-settings__error attendance-settings__error--rule">
                  {fieldErrors.minimumOfficeDaysPerMonth}
                </span>
              )}
            </div>

            <div className="attendance-settings__rule">
              <label
                className="attendance-settings__rule-label"
                htmlFor="shortDayHours"
              >
                Short day threshold
              </label>
              <div className="attendance-settings__rule-control">
                <input
                  id="shortDayHours"
                  type="number"
                  min={HOURS_STEP}
                  max={MAX_HOURS_THRESHOLD}
                  step={HOURS_STEP}
                  className={`attendance-settings__input${
                    fieldErrors.shortDayHoursThreshold
                      ? ' attendance-settings__input--error'
                      : ''
                  }`}
                  value={form.shortDayHoursThreshold}
                  onChange={(event) =>
                    setForm({
                      ...form,
                      shortDayHoursThreshold: Number(event.target.value)
                    })
                  }
                />
                <span className="attendance-settings__unit">hours</span>
              </div>
              {fieldErrors.shortDayHoursThreshold && (
                <span className="attendance-settings__error attendance-settings__error--rule">
                  {fieldErrors.shortDayHoursThreshold}
                </span>
              )}
            </div>

            <div className="attendance-settings__rule">
              <label
                className="attendance-settings__rule-label"
                htmlFor="fullDayHours"
              >
                Full day threshold
              </label>
              <div className="attendance-settings__rule-control">
                <input
                  id="fullDayHours"
                  type="number"
                  min={HOURS_STEP}
                  max={MAX_HOURS_THRESHOLD}
                  step={HOURS_STEP}
                  className={`attendance-settings__input${
                    fieldErrors.fullDayHoursThreshold
                      ? ' attendance-settings__input--error'
                      : ''
                  }`}
                  value={form.fullDayHoursThreshold}
                  onChange={(event) =>
                    setForm({
                      ...form,
                      fullDayHoursThreshold: Number(event.target.value)
                    })
                  }
                />
                <span className="attendance-settings__unit">hours</span>
              </div>
              {fieldErrors.fullDayHoursThreshold && (
                <span className="attendance-settings__error attendance-settings__error--rule">
                  {fieldErrors.fullDayHoursThreshold}
                </span>
              )}
            </div>
          </div>
        </section>

        <section className="attendance-settings__card attendance-settings__card--assignees">
          <div className="attendance-settings__card-header">
            <span className="attendance-settings__card-icon" aria-hidden>
              <GroupOutlinedIcon fontSize="small" />
            </span>
            <div>
              <Typography
                type="heading4"
                text="Upload assignees"
                weight="bold"
              />
              <Typography
                type="small_regular"
                text="Admins can always upload. Add employees who should upload attendance sheets."
                className="attendance-settings__card-hint"
              />
            </div>
          </div>

          <div className="attendance-settings__assignees-panel">
            <div className="attendance-settings__assignees-list">
              {form.assignees.length > 0 ? (
                form.assignees.map((assignee) => (
                  <div
                    key={assignee.userRoleId}
                    className="attendance-settings__assignee-row"
                  >
                    <span
                      className="attendance-settings__assignee-avatar"
                      aria-hidden
                    >
                      {getInitials(getAssigneeName(assignee))}
                    </span>
                    <span className="attendance-settings__assignee-name">
                      {getAssigneeName(assignee)}
                    </span>
                    <button
                      type="button"
                      className="attendance-settings__assignee-remove"
                      aria-label={`Remove ${getAssigneeName(assignee)}`}
                      onClick={() => handleRemoveAssignee(assignee.userRoleId)}
                    >
                      <CloseIcon fontSize="small" />
                    </button>
                  </div>
                ))
              ) : (
                <div className="attendance-settings__assignees-empty">
                  <PersonOutlineIcon fontSize="small" />
                  <span>No upload assignees added yet.</span>
                </div>
              )}
            </div>

            <div
              className={`attendance-settings__dropdown${
                dropdownOpen ? ' attendance-settings__dropdown--open' : ''
              }`}
              ref={searchContainerRef}
            >
              <label
                className="attendance-settings__label"
                htmlFor="assigneeSearch"
              >
                Add employee
              </label>
              <div className="attendance-settings__dropdown-trigger">
                <input
                  ref={searchInputRef}
                  id="assigneeSearch"
                  type="text"
                  className="attendance-settings__dropdown-input"
                  placeholder="Filter by name (optional)"
                  value={searchTerm}
                  onChange={(event) => {
                    setSearchTerm(event.target.value);
                    setDropdownOpen(true);
                  }}
                  onFocus={() => setDropdownOpen(true)}
                />
                <button
                  type="button"
                  className="attendance-settings__dropdown-toggle"
                  aria-label={
                    dropdownOpen
                      ? 'Close employee search'
                      : 'Open employee search'
                  }
                  onClick={() =>
                    dropdownOpen ? closeDropdown() : openDropdown()
                  }
                >
                  <KeyboardArrowDownIcon
                    className={`attendance-settings__dropdown-chevron${
                      dropdownOpen
                        ? ' attendance-settings__dropdown-chevron--open'
                        : ''
                    }`}
                    fontSize="small"
                  />
                </button>
              </div>

              {dropdownOpen && (
                <div
                  className="attendance-settings__dropdown-menu"
                  role="listbox"
                  onScroll={handleAssigneeMenuScroll}
                >
                  {searchLoading ? (
                    <div className="attendance-settings__dropdown-status">
                      <CircularProgress size={18} />
                      <span>Loading employees...</span>
                    </div>
                  ) : searchResults.length === 0 ? (
                    <div className="attendance-settings__dropdown-status">
                      No employees found.
                    </div>
                  ) : (
                    <>
                      {searchResults.map((option) =>
                        renderAssigneeOption(option)
                      )}
                      {loadingMore && (
                        <div className="attendance-settings__dropdown-status">
                          <CircularProgress size={18} />
                          <span>Loading more...</span>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </section>
      </div>

      <footer className="attendance-settings__footer">
        <div className="attendance-settings__footer-left">
          {updatedMeta.updatedAt && (
            <div className="attendance-settings__saved-meta">
              <span className="attendance-settings__saved-icon" aria-hidden>
                <HistoryOutlinedIcon fontSize="small" />
              </span>
              <div className="attendance-settings__saved-copy">
                <span className="attendance-settings__saved-label">
                  Last saved
                </span>
                <span className="attendance-settings__saved-value">
                  {formatAttendanceSavedAt(updatedMeta.updatedAt)}
                </span>
                {(updatedMeta.updatedByDisplayName ||
                  updatedMeta.updatedByUpn) && (
                  <span className="attendance-settings__saved-by">
                    by{' '}
                    {updatedMeta.updatedByDisplayName ||
                      formatSavedByLabel(updatedMeta.updatedByUpn)}
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
        <div className="attendance-settings__actions">
          {isDirty && (
            <span className="attendance-settings__unsaved-text">
              Unsaved changes
            </span>
          )}
          <ButtonPro
            label="Cancel"
            type="white"
            onClick={handleCancel}
            disabled={!isDirty || saving}
          />
          <ButtonPro
            label={saving ? 'Saving...' : 'Save settings'}
            onClick={handleSave}
            disabled={!isDirty || saving || hasErrors}
            isLoading={saving}
          />
        </div>
      </footer>
    </div>
  );
};
